import { FC, Fragment, useMemo, useState } from "react";
// i18next
import { useTranslation } from "react-i18next";
// Redux
import { useAppDispatch, useAppSelector } from "app/hooks/useStore";
// Models
import IBillingInvoice from "app/models/BillingInvoice";
// Async
import { getInvoices } from "app/store/BillingInvoices/BillingInvoices.async";
// Actions
import { BillingInvoicesActions } from "app/store/BillingInvoices/BillingInvoices.slice";
// Selectors
import {
  selectAreAllOnPageSelected, selectBatchInvoices, selectBatchInvoicesIds, selectInvoices,
  selectInvoicesExpandedAll,
  selectInvoicesTotal, selectIsSomeOnPageSelected, selectParams
} from "app/store/BillingInvoices/BillingInvoices.selectors";
// Components
import { IconButton } from "app/components/Mui";
import { Loader, Message } from "app/components/Utilities";
import InvoicesListItem from "./InvoicesListItem";
import InvoicesTableSettings from "./InvoicesTableSettings";
// MUI
import {
  Box,
  Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TablePagination, TableRow,
  Tooltip
} from "@mui/material";
// Icons
import {
  CheckBoxOutlineBlankOutlined as CheckBoxOutlineBlankOutlinedIcon,
  IndeterminateCheckBoxOutlined as IndeterminateCheckBoxOutlinedIcon,
  CheckBoxOutlined as CheckBoxOutlinedIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon
} from '@mui/icons-material';
// Context
import { useInvoiceColumnsContext } from "./InvoiceColumns.context";

const BillingInvoicesList:FC = () => {
  const { t } = useTranslation();
  const { allColumns, isVisible } = useInvoiceColumnsContext();
  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const invoices:IBillingInvoice[] | null = useAppSelector(selectInvoices);
  const params = useAppSelector(selectParams);
  const total = useAppSelector(selectInvoicesTotal);
  const batchInvoices = useAppSelector(selectBatchInvoices);
  const batchInvoicesIds = useAppSelector(selectBatchInvoicesIds);
  const areAllOnPageSelected = useAppSelector(selectAreAllOnPageSelected);
  const isSomeOnPageSelected = useAppSelector(selectIsSomeOnPageSelected);
  const invoicesExpandedAll = useAppSelector(selectInvoicesExpandedAll);

  const [limit, setLimit] = useState<number>(params.limit);
  const [page, setPage] = useState<number>(0);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
    const { dateRangeStart, dateRangeEnd, ...restParams } = params;
    dispatch(getInvoices({ ...restParams, limit, offset: newPage * limit }));
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextLimit = parseInt(event.target.value, 10);
    setLimit(nextLimit);
    setPage(0);
    const { dateRangeStart, dateRangeEnd, ...restParams } = params;
    dispatch(getInvoices({ ...restParams, limit: nextLimit, offset: 0 }));
  };

  const handleSelectAll = () => {
    if (!invoices || !invoices.length) return;

    const invoiceIdsOnPage = new Set(invoices.map(i => i.id));
    const isAnySelectedOnPage = batchInvoicesIds.some(id => invoiceIdsOnPage.has(id));

    if (isAnySelectedOnPage) {
      const newSelected = batchInvoices.filter(invoice => !invoiceIdsOnPage.has(invoice.id));
      dispatch(BillingInvoicesActions.setBatchInvoices(newSelected));
    } else {
      const newInvoicesToAdd = invoices.filter(invoice => !batchInvoicesIds.includes(invoice.id));
      dispatch(BillingInvoicesActions.setBatchInvoices([...batchInvoices, ...newInvoicesToAdd]));
    }
  };

  const handleExpandAll = () => {
    dispatch(BillingInvoicesActions.setInvoicesExpandedAll(!invoicesExpandedAll));
  };

  const columns = useMemo(() => {
    return allColumns.filter(column => isVisible(column.id));
  }, [allColumns, isVisible]);

  if ( !invoices ) return <Loader />;
  if ( !invoices.length ) return <Message text={t('pages.billingAndSubscriptions.noData')} />;
  return (
    <Fragment>
      <TableContainer component={Paper} sx={{ flexGrow: 1 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                  <Tooltip title={areAllOnPageSelected || isSomeOnPageSelected ? t('labels.unselectAll') : t('labels.selectAll')}>
                    <IconButton
                      name="select-all-invoices"
                      onClick={handleSelectAll}
                      disabled={!invoices?.length}
                    >
                      {areAllOnPageSelected
                        ? <CheckBoxOutlinedIcon color="primary" />
                        : isSomeOnPageSelected
                          ? <IndeterminateCheckBoxOutlinedIcon color="primary" />
                          : <CheckBoxOutlineBlankOutlinedIcon />
                      }
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={invoicesExpandedAll ? t('labels.collapseAll') : t('labels.expandAll')}>
                    <IconButton name="expand-all-invoices" onClick={handleExpandAll}>
                      {invoicesExpandedAll ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
              {columns.map((column) => (
                <TableCell key={column.id} sx={{ py: 1, px: 2 }}>{t(column.labelKey)}</TableCell>
              ))}
              <TableCell align="right" sx={{ py: 1, px: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                  {t('labels.actions')}
                  <InvoicesTableSettings />
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {invoices.map((invoice) => (
              <InvoicesListItem key={invoice.id} invoice={invoice} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        sx={{ flexShrink: 0 }}
        count={total}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={limit}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[50, 100, 200, 500]}
        labelRowsPerPage={t('pages.billingAndSubscriptions.invoicesPerPageLabel')}
      />
    </Fragment>
  )
}

export default BillingInvoicesList;
